import { Link } from 'react-router-dom'
import {
  Building2,
  CreditCard,
  Globe2,
  MapPin,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react'
import PageHero from './shared/PageHero'

const pillars = [
  {
    icon: Building2,
    title: 'Multi-city stays',
    body: 'Manauna Dham is our heart — but Aonla Stay also lists verified hotels and homestays across Bareilly region and partner cities. Switch cities on the homepage to explore what is live today.',
  },
  {
    icon: ShieldCheck,
    title: 'Verified hosts',
    body: 'Every listing comes from onboarded vendors with real rooms, photos, and contact details. No anonymous listings — you always know who you are booking with.',
  },
  {
    icon: CreditCard,
    title: 'Secure checkout',
    body: 'Online payments via Cashfree with production-grade partner checkout on live builds. Demo bookings use sandbox mode for safe testing.',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp updates',
    body: 'Booking confirmations and status alerts reach guests, hosts, and our ops team on WhatsApp — so nobody misses a check-in during yatra season.',
  },
]

const steps = [
  { n: '01', title: 'Pick your city', desc: 'Start with Manauna Dham or switch to another city with live inventory.' },
  { n: '02', title: 'Choose dates & guests', desc: 'We suggest dates with lighter booking pressure — default 5 nights, 2 adults.' },
  { n: '03', title: 'Book & pay online', desc: 'Instant confirmation, transparent pricing, and support if plans change.' },
]

const About = () => {
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero
        eyebrow="About Aonla Stay"
        title="Pilgrimage hospitality, built for real travellers"
        subtitle="We help devotees and families find trusted stays near Manauna Dham — and growing listings across North India — with modern booking, not phone-tag."
      />

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600 mb-3">Our mission</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight leading-tight">
              Less stress before darshan. More time with family.
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed text-lg">
              Aonla Stay is a booking platform focused on pilgrimage travel — starting with Shri Khatu Shyam Ji
              Manauna Dham near Aonla, Bareilly. We aggregate vendor rooms, homestays, and yatra services so you
              can compare, book, and pay in one place.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              We are not the temple authority. We are hospitality partners: listing verified stays, coordinating
              with local hosts, and keeping you informed from search to check-out.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/rooms?location=Manauna"
                className="inline-flex items-center px-6 py-3 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors"
              >
                Browse Manauna stays
              </Link>
              <Link
                to="/manauna-dham"
                className="inline-flex items-center px-6 py-3 rounded-xl border border-gray-300 bg-white text-gray-800 font-semibold hover:border-gray-400 transition-colors"
              >
                Manauna Dham guide
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary-200/40 to-transparent rounded-3xl blur-2xl" />
            <img
              src={`${import.meta.env.BASE_URL}manauna-dham/night.webp`}
              alt="Manauna Dham region"
              className="relative rounded-3xl shadow-2xl w-full aspect-[4/3] object-cover ring-1 ring-black/5"
            />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-white border-y border-gray-200/80 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600 mb-2">Why book with us</p>
            <h2 className="text-3xl font-bold text-gray-900">Platform built for yatra season</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-gray-200 bg-[#fafafa] p-6 hover:border-primary-200 hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-primary-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600 mb-2">How it works</p>
            <h2 className="text-3xl font-bold text-gray-900">Three steps to a confirmed stay</h2>
            <p className="mt-3 text-gray-600">Same flow on mobile and desktop — designed like modern travel apps you already trust.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl bg-white border border-gray-200 p-6 shadow-sm">
                <span className="text-3xl font-black text-primary-200">{s.n}</span>
                <h3 className="mt-3 font-bold text-gray-900">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="bg-[#1a1612] text-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <Globe2 className="h-8 w-8 text-primary-400 mb-4" />
              <h2 className="text-2xl font-bold">Where we operate</h2>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                Priority market: Manauna / Manona Dham, Aonla, Bareilly UP. Expanding city-by-city as vendors onboard.
              </p>
            </div>
            <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
              {[
                { label: 'Manauna Dham', desc: 'Pilgrimage hotels, dharamshalas, yatra services', href: '/manauna-dham' },
                { label: 'Aonla & Bareilly', desc: 'Family hotels, transit stays, event venues', href: '/rooms?location=Aonla' },
                { label: 'Partner cities', desc: 'Vendor listings as hosts join the platform', href: '/vendors' },
                { label: 'Packages & blogs', desc: 'Yatra packages and Manauna travel guides', href: '/blogs' },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="group rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 hover:border-primary-400/40 transition-all"
                >
                  <h3 className="font-semibold text-white group-hover:text-primary-300">{item.label}</h3>
                  <p className="mt-1 text-sm text-white/60">{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team ethos + contact */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="rounded-3xl bg-gradient-to-br from-primary-50 to-white border border-primary-100 p-8 sm:p-12 lg:p-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-800 mb-4">
                <Sparkles className="h-3.5 w-3.5" /> Built in Bareilly region
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Local team, national standards</h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                We combine on-ground knowledge of Manauna Dham travel with product engineering from Naaz Apps —
                so devotees get both warmth and reliability.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Users className="h-4 w-4 text-primary-600" /> Vendor onboarding & room quality checks</li>
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary-600" /> Manauna village, Tehsil Aonla, Bareilly UP 243301</li>
              </ul>
            </div>
            <div className="flex flex-col gap-4">
              <a href="tel:9457425914" className="flex items-center justify-between rounded-2xl bg-white border border-gray-200 px-5 py-4 hover:border-primary-300 transition-colors">
                <span className="font-semibold text-gray-900">Call us</span>
                <span className="text-primary-700 font-bold">9457425914</span>
              </a>
              <a href="mailto:info@aonlastay.napps.in" className="flex items-center justify-between rounded-2xl bg-white border border-gray-200 px-5 py-4 hover:border-primary-300 transition-colors">
                <span className="font-semibold text-gray-900">Email</span>
                <span className="text-primary-700 font-bold text-sm">info@aonlastay.napps.in</span>
              </a>
              <Link to="/contact" className="text-center rounded-2xl bg-gray-900 text-white font-semibold px-5 py-4 hover:bg-gray-800 transition-colors">
                Contact form →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
